import React from "react";
import { Mail, Phone, Clock, Gavel, ChevronRight, ShieldCheck, ExternalLink, UserCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";

const GrievanceRedressalComponent = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  const escalationMatrix = [
    { level: "01", role: "Customer Care", name: "Deepti Sarkar", email: "deeptisarkar7@gmail.com", phone: "+91 9082280240" },
    { level: "02", role: "Head of Customer Care", name: "Deepti Sarkar", email: "deeptisarkar7@gmail.com", phone: "+91 9082280240" },
    { level: "03", role: "Compliance Officer", name: "Deepti Sarkar", email: "deeptisarkar7@gmail.com", phone: "+91 9082280240" },
    { level: "04", role: "CEO", name: "Deepti Sarkar", email: "deeptisarkar7@gmail.com", phone: "+91 9082280240" },
    { level: "05", role: "Principal Officer", name: "Deepti Sarkar", email: "deeptisarkar7@gmail.com", phone: "+91 9082280240" },
  ];

  return (
    <div className="w-full bg-white overflow-x-hidden font-sans text-slate-900">

    <Helmet>
  <title>Grievance Redressal Mechanism| Option Bricks</title>

</Helmet>


      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        
        {/* Header Section */}
        <motion.div {...fadeInUp} className="text-center mb-12 md:mb-16">
          <h1 className="text-3xl md:text-5xl font-black mb-6 tracking-tight leading-tight">
            Grievance Redressal Mechanism
          </h1>
          <p className="text-slate-500 max-w-3xl mx-auto text-base md:text-lg px-2">
            We value your trust and are committed to addressing any concerns or complaints promptly and transparently. 
            Clients may reach out to us through any of the following channels:
          </p>
        </motion.div>

        {/* Primary Contact Channels */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-12">
          <motion.div 
            whileHover={{ y: -5 }} 
            className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div className="p-4 bg-blue-50 rounded-2xl mb-4 sm:mb-0 sm:mr-6">
              <Mail className="text-blue-600" size={28} />
            </div>
            <div className="min-w-0 w-full">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Email Support</h2>
              <a href="mailto:compliance@optionbricks.in" className="text-lg md:text-xl font-bold text-slate-800 hover:text-blue-600 transition-colors break-words">
                compliance@optionbricks.in
              </a>
              <p className="text-sm text-slate-500 mt-2">Write to us for any queries or grievances.</p>
            </div>
          </motion.div>

          <motion.div 
            whileHover={{ y: -5 }} 
            className="bg-white p-6 md:p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left"
          >
            <div className="p-4 bg-emerald-50 rounded-2xl mb-4 sm:mb-0 sm:mr-6">
              <Phone className="text-emerald-600" size={28} />
            </div>
            <div className="min-w-0 w-full">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Phone Support</h3>
              <a href="tel:+919082280240" className="text-lg md:text-xl font-bold text-slate-800 hover:text-emerald-600 transition-colors">
                +91-9082280240
              </a>
              <p className="text-sm text-slate-500 mt-2">Available during working hours.</p>
            </div>
          </motion.div>
        </div>

        {/* Resolution Timeline Alert */}
        <motion.div 
          {...fadeInUp} 
          className="bg-slate-900 rounded-[2rem] p-6 md:p-10 mb-16 relative overflow-hidden group shadow-xl"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
          
          <div className="flex flex-col lg:flex-row items-center justify-between z-10 relative gap-6">
            <div className="flex flex-col md:flex-row items-center text-center md:text-left">
              <div className="p-4 bg-slate-800 rounded-2xl mb-4 md:mb-0 md:mr-6">
                <Clock className="text-blue-400" size={32} />
              </div>
              <div>
                <h4 className="text-white text-xl md:text-2xl font-bold mb-2">Standard Resolution Timeline</h4>
                <p className="text-slate-400 text-sm md:text-base leading-relaxed max-w-md">
                  We aim to provide a resolution within <span className="text-blue-400 font-semibold">21 working days</span> from the date of receiving your complaint.
                </p>
              </div>
            </div>
            <div className="shrink-0 w-full lg:w-auto">
              <div className="bg-blue-600/20 border border-blue-500/40 text-blue-400 px-6 py-4 rounded-2xl font-black text-sm uppercase tracking-widest text-center">
                21 Working Days
              </div>
            </div>
          </div>
        </motion.div>

        {/* Escalation Matrix Section */}
        <motion.section {...fadeInUp} className="mb-16">
          <div className="flex flex-col items-center md:items-start md:flex-row md:items-end justify-between mb-10 gap-6">
            <div className="flex flex-col md:flex-row items-center text-center md:text-left">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 md:mb-0 md:mr-5 shadow-lg shadow-blue-200 shrink-0">
                <ShieldCheck className="text-white" size={28} />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight text-left">Escalation Matrix</h2>
                <p className="text-slate-500 text-sm font-medium mt-1">
                  Key contacts based on the nature or escalation level of your concern
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Desktop Header Row */}
            <div className="hidden lg:grid grid-cols-12 px-8 mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <div className="col-span-3">Designation</div>
              <div className="col-span-3">Contact Person</div>
              <div className="col-span-3">Direct Contact</div>
              <div className="col-span-3 text-right">Working Hours</div>
            </div>

            {/* Matrix Cards */}
            {escalationMatrix.map((item, idx) => (
              <motion.div
                key={idx}
                whileHover={{ x: 5 }}
                className="bg-white border border-slate-200 rounded-[1.5rem] md:rounded-[2rem] p-5 lg:p-8 shadow-sm hover:shadow-md hover:border-blue-200 transition-all group"
              >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-0 items-center">
                  
                  {/* Designation */}
                  <div className="lg:col-span-3 flex items-center">
                    <span className="text-blue-600 font-black text-xs mr-4 bg-blue-50 w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                      {item.level}
                    </span>
                    <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                      {item.role}
                    </h3>
                  </div>

                  {/* Contact Person */}
                  <div className="lg:col-span-3 flex items-center text-slate-600">
                    <UserCircle size={18} className="mr-2 text-slate-300 shrink-0" />
                    <span className="font-medium truncate">{item.name}</span>
                  </div>

                  {/* Contact Details */}
                  <div className="lg:col-span-3 flex flex-col space-y-1">
                    <a href={`tel:${item.phone}`} className="text-sm font-bold text-slate-800 hover:text-blue-600 transition-colors">
                      {item.phone}
                    </a>
                    <a href={`mailto:${item.email}`} className="text-xs text-slate-400 hover:text-blue-600 transition-colors truncate">
                      {item.email}
                    </a>
                  </div>

                  {/* Timing */}
                  <div className="lg:col-span-3 flex items-center justify-between">
                    <div className="flex items-center text-[11px] font-bold text-slate-500 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                      <Clock size={14} className="mr-2 text-slate-400" />
                      Mon–Fri, 10:00 AM – 6:00 PM
                    </div>
                    <ChevronRight className="text-slate-200 group-hover:text-blue-400 group-hover:translate-x-1 transition-all hidden lg:block" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Final Escalation: Still Not Satisfied? */}
        <motion.section {...fadeInUp} className="mt-16 md:mt-24">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-black text-slate-900 mb-4 px-2">Still Not Satisfied?</h2>
            <p className="text-slate-500 max-w-2xl mx-auto text-sm md:text-base px-4 leading-relaxed">
              If your grievance remains unresolved even after contacting the above authorities, 
              you may seek further redressal through these official regulatory portals:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* SEBI SCORES Portal */}
            <motion.div 
              whileHover={{ y: -8 }}
              className="group p-8 md:p-10 bg-gradient-to-br from-blue-50 to-white rounded-[2rem] md:rounded-[2.5rem] border border-blue-100 shadow-sm transition-all"
            >
              <div className="mb-6 inline-flex items-center justify-center w-14 h-14 bg-white rounded-2xl shadow-sm border border-blue-100 text-blue-600">
                <ShieldCheck size={32} />
              </div>
              <h3 className="text-2xl font-bold text-blue-900 mb-4">SEBI SCORES Portal</h3>
              <p className="text-blue-800/70 text-sm mb-8 leading-relaxed">
                Raise your concern through SEBI’s SCORES Portal (SEBI Complaints Redress System), 
                a centralized grievance system for facilitating effective redressal in a time-bound manner.
              </p>
              <a 
                href="https://scores.sebi.gov.in/" 
                target="_blank" 
                rel="noreferrer" 
                className="inline-flex items-center justify-center w-full sm:w-auto bg-blue-600 text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-200 transition-all"
              >
                Lodge Complaint <ExternalLink size={16} className="ml-2" />
              </a>
            </motion.div>

            {/* SMART ODR Platform */}
            <motion.div 
              whileHover={{ y: -8 }}
              className="group p-8 md:p-10 bg-gradient-to-br from-amber-50 to-white rounded-[2rem] md:rounded-[2.5rem] border border-amber-100 shadow-sm transition-all"
            >
              <div className="mb-6 inline-flex items-center justify-center w-14 h-14 bg-white rounded-2xl shadow-sm border border-amber-100 text-amber-600">
                <Gavel size={32} />
              </div>
              <h3 className="text-2xl font-bold text-amber-900 mb-4">SMART ODR Platform</h3>
              <p className="text-amber-800/70 text-sm mb-8 leading-relaxed">
                Still dissatisfied? Proceed to the Online Dispute Resolution (ODR) portal. 
                A common ODR Portal is established to harness online conciliation and arbitration.
              </p>
              <a 
                href="https://smartodr.in" 
                target="_blank" 
                rel="noreferrer" 
                className="inline-flex items-center justify-center w-full sm:w-auto bg-amber-600 text-white px-8 py-4 rounded-xl font-bold text-sm hover:bg-amber-700 hover:shadow-lg hover:shadow-amber-200 transition-all"
              >
                Visit SmartODR <ExternalLink size={16} className="ml-2" />
              </a>
            </motion.div>
          </div>

          {/* Trust Footer */}
          <div className="mt-16 md:mt-24 py-8 border-t border-slate-100 text-center">
            <p className="text-[10px] md:text-xs text-slate-400 font-bold uppercase tracking-[0.3em] px-4">
              Protecting Investor Interests • Transparency • Integrity
            </p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default GrievanceRedressalComponent;